import { useContext, useMemo } from 'react';
import toast from 'react-hot-toast';
import { AuthContext } from '../context/AuthContext';
import { buildPermissionSet, hasPermission } from '../utils/permissions';

/**
 * usePermission — RBAC hook
 *
 * Usage:
 *   const { can, requirePermission } = usePermission();
 *
 *   // Just check (returns boolean):
 *   if (can('pbl', 'export')) { ... }
 *
 *   // Guard a handler (shows toast + skips if not permitted):
 *   const handleDelete = requirePermission('pbl', 'delete', () => { doDelete(); });
 *   <Button onClick={handleDelete}>Delete</Button>
 */
export default function usePermission() {
  const { userData } = useContext(AuthContext);

  const isSuperUser = Boolean(userData?.is_superuser);

  // Build the permission Set once per userData change
  const permissionSet = useMemo(
    () => buildPermissionSet(userData),
    [userData]
  );

  /**
   * Check if the current user has permission for module + action.
   * Superusers always return true.
   *
   * @param {string} module
   * @param {string} action
   * @returns {boolean}
   */
  const can = (module, action) => {
    if (isSuperUser) return true;
    // If no module/action provided, default allow (un-guarded pages)
    if (!module || !action) return true;
    return hasPermission(permissionSet, module, action);
  };

  /**
   * Wraps an event handler with a permission check.
   * If the user doesn't have permission, shows a toast and returns early.
   * If permissionModule or permissionAction is null/undefined, the handler passes through.
   *
   * @param {string|null} module
   * @param {string|null} action
   * @param {Function} handler - the original callback
   * @returns {Function} - wrapped handler safe to pass as onClick etc.
   */
  const requirePermission = (module, action, handler) => {
    return (...args) => {
      if (can(module, action)) {
        return handler(...args);
      }
      toast.error("You don't have permission to perform this action.", {
        id: `perm-denied-${module}-${action}`,  // dedup toasts
        duration: 3000,
        style: {
          borderRadius: '8px',
          background: '#1e1e2e',
          color: '#ff6b6b',
          border: '1px solid #ff6b6b44',
          fontWeight: 500,
        },
        iconTheme: {
          primary: '#ff6b6b',
          secondary: '#1e1e2e',
        },
      });
    };
  };

  return { can, requirePermission, isSuperUser, permissionSet };
}
